/**
 * Formatação do WhatsApp no texto das mensagens: *negrito*, _itálico_, ~riscado~, `código` e ```bloco```.
 * Mesma regra do aplicativo: a marca abre no começo do texto ou depois de algo que não é letra/número,
 * fecha antes de algo que não é letra/número, encosta no texto (sem espaço por dentro) e não atravessa
 * linhas (só o bloco ```). Assim "2*3*4" e "nome_do_arquivo" ficam como estão.
 * Mesma regra do backend (attendance/application/whatsapp-format.ts, histórico no Agidesk).
 */
export interface Styled {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
}

const MARKS: Record<string, 'bold' | 'italic' | 'strike'> = { '*': 'bold', _: 'italic', '~': 'strike' };
const WORD = /[\p{L}\p{N}]/u;
const SPACE = /\s/;

const opensAt = (text: string, i: number) => (i === 0 || !WORD.test(text[i - 1]!)) && i + 1 < text.length && !SPACE.test(text[i + 1]!);

/** Onde fecha a marca aberta em `i` (mesma linha), ou -1. */
function closeOf(text: string, i: number): number {
  const mark = text[i];
  for (let j = i + 2; j < text.length && text[j] !== '\n'; j++) {
    if (text[j] === mark && !SPACE.test(text[j - 1]!) && (j + 1 === text.length || !WORD.test(text[j + 1]!))) return j;
  }
  return -1;
}

export function parseWhatsAppFormatting(text: string, style: Omit<Styled, 'text'> = {}): Styled[] {
  const out: Styled[] = [];
  let plain = '';
  const flush = () => {
    if (plain) out.push({ ...style, text: plain });
    plain = '';
  };
  let i = 0;
  while (i < text.length) {
    if (text.startsWith('```', i)) {
      const end = text.indexOf('```', i + 3);
      if (end > i + 3) {
        flush();
        out.push({ ...style, code: true, text: text.slice(i + 3, end) });
        i = end + 3;
        continue;
      }
    }
    const c = text[i]!;
    if ((c === '`' || c in MARKS) && opensAt(text, i)) {
      const end = closeOf(text, i);
      if (end > 0) {
        flush();
        const inner = text.slice(i + 1, end);
        if (c === '`') out.push({ ...style, code: true, text: inner });
        else out.push(...parseWhatsAppFormatting(inner, { ...style, [MARKS[c]!]: true }));
        i = end + 1;
        continue;
      }
    }
    plain += c;
    i++;
  }
  flush();
  return out;
}
